import React, { useEffect, useState } from 'react';
import Barcode from 'react-barcode';
import { getFile } from 'interfaces/user';

interface ProductLabelsProps {
    products: any[];
    onClose: () => void;
}

const ProductLabels: React.FC<ProductLabelsProps> = ({ products, onClose }) => {
    const [companyLogo, setCompanyLogo] = useState<string>('');

    useEffect(() => {
        // Get company logo from API
        const initCompanyLogo = async () => {
            try {
                const res = await getFile('company-logo.jpg');
                if (res && res.data && res.data.url) {
                    setCompanyLogo(res.data.url);
                }
            } catch (error) {
                console.error('Erreur lors du chargement du logo:', error);
            }
        };
        initCompanyLogo();
    }, []);

    const calculateUnitPrice = (product: any) => {
        if (!product.subUnitQuantity) return product.price || 0;
        const totalPrice = product.price || 0;
        const subQuantity = parseFloat(product.subUnitQuantity) || 1;
        return (totalPrice / subQuantity).toFixed(2);
    };

    const calculateTTC = (priceHT: number, vat: number = 20) => {
        return (priceHT * (1 + vat / 100)).toFixed(2);
    };

    const handlePrint = () => {
        window.print();
    };


    // Cut products pages 12 prints (2x6)
    const pages: any[][] = [];
    for (let i = 0; i < products.length; i += 12) {
        pages.push(products.slice(i, i + 12));
    }


    return (
        <div className="fixed inset-0 z-[9999] bg-white overflow-auto">
            {/* Control buttons - hidden when printing */}
            <div className="no-print fixed top-4 right-4 z-[10000] flex gap-2">
                <button
                    onClick={handlePrint}
                    className="rounded-xl bg-green-600 px-6 py-3 text-white font-medium hover:bg-green-700 shadow-lg"
                >
                    Imprimer
                </button>
                <button
                    onClick={onClose}
                    className="rounded-xl bg-gray-600 px-6 py-3 text-white font-medium hover:bg-gray-700 shadow-lg"
                >
                    Fermer
                </button>
            </div>

            {/* Label pages */}
            <div className="pt-16">
                {pages.map((pageProducts, pageIndex) => (
                    <div
                        key={pageIndex}
                        className="page-break mx-auto"
                        style={{
                            width: '210mm',
                            minHeight: '297mm',
                            padding: '5mm',
                            backgroundColor: 'white',
                            pageBreakAfter: pageIndex < pages.length - 1 ? 'always' : 'auto',
                        }}
                    >
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gridTemplateRows: 'repeat(6, 1fr)',
                                gap: '3mm',
                                height: '287mm',
                            }}
                        >
                            {pageProducts.map((product, index) => (
                                <div
                                    key={`${pageIndex}-${index}-${product._id || index}`}
                                    style={{
                                        border: '1px solid #ddd',
                                        padding: '2mm',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        backgroundColor: 'white',
                                        overflow: 'hidden',
                                    }}
                                >
                                {/* Logo */}
                                {companyLogo && (
                                    <div style={{ 
                                        textAlign: 'center',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}>
                                        <img
                                            src={companyLogo}
                                            alt="Logo"
                                            style={{
                                                maxHeight: '12mm',
                                                maxWidth: '80%',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </div>
                                )}

                                {/* Product name */}
                                <div
                                    style={{
                                        borderTop: '1.5px solid #000',
                                        borderBottom: '1.5px solid #000',
                                        padding: '1mm 0',
                                        margin: '1mm 0',
                                    }}
                                >
                                    <p
                                        style={{
                                            fontSize: '10pt',
                                            fontWeight: 'bold',
                                            textAlign: 'center',
                                            textTransform: 'uppercase',
                                            lineHeight: '1.1',
                                            margin: 0,
                                        }}
                                    >
                                        {product.name}
                                    </p>
                                </div>

                                {/*     Barcode and Price */}
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: '2mm',
                                    }}
                                >
                                    {/* Barcode */}
                                    <div style={{ flex: '0 0 55%' }}>
                                        {product.ean || product.ref ? (
                                            <div>
                                                <Barcode
                                                    value={product.ean || product.ref || '0000000000000'}
                                                    height={30}
                                                    width={1}
                                                    fontSize={8}
                                                    displayValue={false}
                                                />
                                                <p
                                                    style={{
                                                        fontSize: '7pt',
                                                        textAlign: 'center',
                                                        margin: '0.5mm 0 0 0',
                                                    }}
                                                >
                                                    {product.ean || product.ref}
                                                </p>
                                            </div>
                                        ) : (
                                            <div
                                                style={{
                                                    height: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '8pt',
                                                    color: '#999',
                                                }}
                                            >
                                                Pas de code EAN
                                            </div>
                                        )}
                                    </div>

                                    {/* Prix */}
                                    <div
                                        style={{
                                            flex: '0 0 40%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.5mm',
                                        }}
                                    >
                                        <div>
                                            <p
                                                style={{
                                                    fontSize: '7pt',
                                                    margin: 0,
                                                    color: '#666',
                                                }}
                                            >
                                                Prix HT:
                                            </p>
                                            <p
                                                style={{
                                                    fontSize: '9pt',
                                                    fontWeight: 'bold',
                                                    margin: 0,
                                                }}
                                            >
                                                {(product.price || 0).toFixed(2)} €
                                            </p>
                                        </div>
                                        <div style={{ display: 'flex', gap: '2mm', justifyContent: 'space-between' }}>
                                            <div style={{ flex: '0 0 48%' }}>
                                                <p
                                                    style={{
                                                        fontSize: '7pt',
                                                        margin: 0,
                                                        color: '#666',
                                                    }}
                                                >
                                                    Prix TTC:
                                                </p>
                                                <p
                                                    style={{
                                                        fontSize: '9pt',
                                                        fontWeight: 'bold',
                                                        margin: 0,
                                                    }}
                                                >
                                                    {calculateTTC(product.price || 0, product.vat || 20)} €
                                                </p>
                                            </div>
                                            <div style={{ flex: '0 0 48%' }}>
                                                <p
                                                    style={{
                                                        fontSize: '7pt',
                                                        margin: 0,
                                                        color: '#666',
                                                    }}
                                                >
                                                    Prix unit.:
                                                </p>
                                                <p
                                                    style={{
                                                        fontSize: '9pt',
                                                        fontWeight: 'bold',
                                                        margin: 0,
                                                    }}
                                                >
                                                    {calculateUnitPrice(product)} €
                                                    {product.subUnity && ` / ${product.subUnity}`}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                            {/* Fill empty cells */}
                            {Array.from({ length: 12 - pageProducts.length }).map((_, index) => (
                                <div
                                    key={`empty-${pageIndex}-${index}`}
                                    style={{
                                        border: '1px dashed #ddd',
                                        backgroundColor: '#fafafa',
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Print styles */}
            <style>{`
                @media print {
                    @page {
                        size: A4;
                        margin: 0;
                    }
                    
                    body {
                        margin: 0;
                        padding: 0;
                    }
                    
                    .no-print {
                        display: none !important;
                    }
                    
                    .page-break {
                        page-break-after: always;
                        break-after: page;
                    }
                    
                    .page-break:last-child {
                        page-break-after: auto;
                        break-after: auto;
                    }
                }
                
                @media screen {
                    .page-break {
                        margin-bottom: 20px;
                        box-shadow: 0 0 10px rgba(0,0,0,0.1);
                    }
                }
            `}</style>
        </div>
    );
};

export default ProductLabels;
